<template>
  <div class="agent-loader">
    <div class="scene">
      <div class="halo" />

      <div class="agent" aria-hidden="true">
        <img :src="agentIcon" alt="" />
      </div>
    </div>
    <span class="shimmer-text text-body-2 agent-status">{{ status || t('chat.thinking') }}</span>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { computed } from 'vue'
import { useTheme } from '../../composables/useTheme.js'
import iconDarkAnimated from '../../assets/icons/rpilot-dark-animated.svg'
import iconLightAnimated from '../../assets/icons/rpilot-light-animated.svg'

// Optional reasoning status. When present it replaces the default
// "Thinking..." label, giving a live update of what the agent is reasoning
// about while the tool timeline is hidden. Wraps to as many lines as it needs
// so the whole line can be read.
defineProps({
  status: { type: String, default: '' },
})

const { isDark } = useTheme()
const agentIcon = computed(() => (isDark() ? iconDarkAnimated : iconLightAnimated))
</script>

<style scoped>
.agent-loader {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 10px;
  /* Fill the message row — the status wraps within it — and allow shrinking
     below the text's natural width so it can never push the layout. The
     vertical padding gives the halo room around the 36px icon. */
  flex: 1 1 auto;
  min-width: 0;
  padding: 4px 0;
}

/* The icon box is exactly the icon (36px), flush with the bubbles' left edge;
   the halo overflows it freely. It must never shrink — a long status has to
   wrap itself, not squeeze the icon (a shrinking box is what used to push the
   robot off the left edge and under the text). */
.scene {
  position: relative;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
}

/* Halo */
.halo {
  position: absolute;
  inset: 50% auto auto 50%;
  width: 44px;
  height: 44px;
  margin: -22px 0 0 -22px;
  border-radius: 50%;
  background: radial-gradient(circle at center, rgba(var(--v-theme-on-surface), 0.15) 0%, transparent 65%);
  animation: halo 2.4s ease-in-out infinite;
  pointer-events: none;
}
@keyframes halo {
  0%, 100% { transform: scale(0.9); opacity: 0.7; }
  50%      { transform: scale(1.1); opacity: 1; }
}

/* Robot agent */
.agent {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 36px;
  height: 36px;
  margin: -18px 0 0 -18px;
  color: rgba(var(--v-theme-on-surface), 0.5);
  display: grid;
  place-items: center;
  animation: breathe 2.4s ease-in-out infinite;
  z-index: 2;
}
.agent img {
  width: 100%;
  height: 100%;
  display: block;
  transform-origin: 50% 65%;
  animation: nod 3.2s ease-in-out infinite;
}

@keyframes breathe {
  0%, 100% { transform: scale(0.96); }
  50%      { transform: scale(1.05); }
}
@keyframes nod {
  0%, 100% { transform: rotate(-5deg); }
  50%      { transform: rotate(5deg); }
}

/* The status wraps to as many lines as it needs so a long reasoning line can
   be read in full. Anchored to the icon: the 8px top padding centres the first
   20px line on the 36px icon and further lines flow below it. Long unbroken
   tokens (ids, URLs) may break anywhere rather than overflow the row. */
.agent-status {
  min-width: 0;
  flex: 1 1 auto;
  padding-top: 8px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Shimmer text */
.shimmer-text {
  background: linear-gradient(
    90deg,
    rgba(var(--v-theme-on-surface), 0.4) 0%,
    rgba(var(--v-theme-on-surface), 0.4) 40%,
    rgba(var(--v-theme-on-surface), 0.9) 50%,
    rgba(var(--v-theme-on-surface), 0.4) 60%,
    rgba(var(--v-theme-on-surface), 0.4) 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: text-shimmer 1.4s ease-in-out infinite;
}
@keyframes text-shimmer {
  0%   { background-position: 100% 50%; }
  100% { background-position: -100% 50%; }
}

@media (prefers-reduced-motion: reduce) {
  .halo, .agent, .agent img, .shimmer-text { animation: none; }
}
</style>
